<script setup lang="ts">
import CardHeading from "../CardHeading.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import { useCalculationTypes } from "@/dataProviders/CalculationTypes";
import { useConfiguration } from "@/dataProviders/Configuration";
import { useEmissionFactorSources } from "@/dataProviders/EmissionFactorSources";
import { DenominatorUnitType, IndicatorTypeType, useIndicators } from "@/dataProviders/Indicators";
import { EmissionFactorType, WizardProjectType } from "@/features/BuildingProject";
import useInstallationEmissionFactors from "@/features/BuildingProject/providers/InstallationEmissionFactorsProvider";
import EmissionConsumptionChoice from "@/features/EmissionConsumptionChoice";
import TransportFuelMultipleForm from "@/features/TransportMappingList/components/Inputs/TransportFuelMultipleForm.vue";
import { Country_Enum, Emission_Factor_Type_Enum } from "@/graphql";
import { useLocalization } from "@/providers/Localization";
import _ from "lodash";
import { storeToRefs } from "pinia";
import { computed, onMounted, watch } from "vue";
import { Flame, Fuel, Recycle, Zap } from "@lucide/vue";
import MachineryFuelEmissionFactorTable from "./MachineryFuelEmissionFactorTable.vue";
import StepIntro from "../StepIntro/StepIntro.vue";
import calculationTypeVariant from "@/services/enums/calculationTypeVariant";
import { useWizardProject } from "@/features/BuildingProject/composables/useWizardProject";

const props = defineProps<{
  wasValidated: boolean;
  project: WizardProjectType;
}>();

const emissionFactorSources = useEmissionFactorSources();
const configuration = useConfiguration();
const localization = useLocalization();
const installationEmissionFactors = useInstallationEmissionFactors();
const { items: machineryFuelEmissionFactorItems, transportSources } = storeToRefs(installationEmissionFactors);

const wastagePercentageDescription = computed(() => {
  const country = localization.getCountry();

  if (country && [Country_Enum.NORWAY, Country_Enum.SWEDEN].includes(country)) {
    return `When setting an average waste %, the A5 data from the EPD or generic data is overwritten with the percentage
                    multiplied by the emissions from the stages A1-A3 & A4 for each material.`;
  }

  return `When setting an average waste %, the A5 data from the EPD or generic data is overwritten with the percentage
                    multiplied by the emissions from the stages A1-A3, A4, C3 and C4 for each material.`;
});

const { project, projectId, isExistingProject, lens } = useWizardProject(() => props.project);

/** A5's four consumption rows live under one object, so one lens serves every write-back below. */
const constructionalEnergy = lens(
  (current) => current.emissionCalculationAssumptions.projectEnergyData.constructional,
  (current, value) => (current.emissionCalculationAssumptions.projectEnergyData.constructional = value)
);

/** A row with nothing in it, for a project that has never had one. Only ever read: see below. */
const emptyEmissionFactor = (): EmissionFactorType => ({ sourceId: null, type: "", value: null });

/**
 * The three consumption rows, read from and written to the stored project.
 *
 * They used to be refs seeded once from a destructure at setup, with a deep watcher each copying them back
 * afterwards. Anything that changed the stored rows while this step was mounted was invisible here, and the
 * next edit would have pushed the stale copy back over it.
 *
 * No materialising of empty rows is needed, and none happens: EmissionConsumptionChoice emits
 * `{ ...props.modelValue, [key]: value }`, a whole new row every time, and never edits one in place. That also
 * makes the `deep: true` on the watchers this replaces vestigial.
 */
const consumptionRow = (key: "heat" | "electricity" | "machineryFuel") =>
  computed<EmissionFactorType>({
    get: () => constructionalEnergy.value?.[key] ?? emptyEmissionFactor(),
    set: (value) => {
      constructionalEnergy.value = { ...constructionalEnergy.value, [key]: value };
    },
  });

const heatData = consumptionRow("heat");
const eletricityData = consumptionRow("electricity");
const machineryFuelData = consumptionRow("machineryFuel");

const calculationTypes = useCalculationTypes();
const indicators = useIndicators();

onMounted(() => {
  if (isExistingProject.value && projectId.value) {
    installationEmissionFactors.fetchItems(projectId.value);
  }
});
const calculationTypeId = computed(() => {
  return props.project.emissionCalculationAssumptions.calculationType.calculationTypeEnum;
});

const calculationTypeIndicator = computed(() => {
  const calculationType = calculationTypes.getCalculationTypes().find((item) => item.id === calculationTypeId.value);

  return {
    numerator: calculationType?.thresholdIndicator.indicatorType as IndicatorTypeType,
    denominator: calculationType?.thresholdIndicator.denominatorUnit as DenominatorUnitType,
  };
});
const considerationPeriod = computed(() => {
  return project.value.emissionCalculationAssumptions.calculationType.considerationPeriod || 0;
});

const yearOfCommissioning = computed(
  () => project.value.emissionCalculationAssumptions?.emissionMetaData?.yearOfCommissioning ?? 0
);

const yearOfConstruction = computed(
  () => project.value.emissionCalculationAssumptions?.emissionMetaData?.yearOfConstruction ?? 0
);

const totalArea = computed(() => {
  return (
    (project.value.emissionCalculationAssumptions.referencedAreas.areaAboveGround ?? 0) +
    (project.value.emissionCalculationAssumptions.referencedAreas.areaBelowGround || 0)
  );
});

const isBR2025 = computed(() => {
  if (calculationTypeId.value === null) {
    return null;
  }

  const item = calculationTypes.getSelectedCalculationType(calculationTypeId.value);

  return item?.variant?.variant === calculationTypeVariant.BR182025;
});

watch(
  calculationTypeId,
  (newCalculationTypeId) => {
    if (newCalculationTypeId) {
      installationEmissionFactors.fetchSources(newCalculationTypeId);
    }
  },
  {
    immediate: true,
  }
);
</script>

<template>
  <div class="w-full">
    <StepIntro
      class="mb-6"
      heading="What does the construction site use?"
      hint="A5, installation. Energy, fuel and earth moved on site during construction, plus material wastage." />
    <!-- Each visibility condition sits on its card so a hidden field never leaves an orphaned heading. -->
    <div class="flex w-full flex-col gap-3">
      <div
        v-if="configuration.showInstallationField('heatSupply', calculationTypeId)"
        class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <Flame
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Heat supply</CardHeading>
          </div>
        </div>
        <div class="grid grid-cols-12 gap-4">
          <EmissionConsumptionChoice
            v-model="heatData"
            :was-validated="props.wasValidated"
            :emission-factor-sources="emissionFactorSources.getHeatSupplySources()"
            :custom-emission-factor-sources="emissionFactorSources.getHeatSupplyCustomSources()"
            :emission-factor-types="[
              Emission_Factor_Type_Enum.DISTRICT_HEATING,
              Emission_Factor_Type_Enum.LINE_GAS,
              Emission_Factor_Type_Enum.ELECTRICITY,
            ]"
            :year-of-commissioning="yearOfCommissioning"
            :year-of-construction="yearOfConstruction"
            :consideration-period="considerationPeriod"
            :country="project.buildingAddress?.country"
            variant="installation" />
        </div>
      </div>

      <div
        v-if="configuration.showInstallationField('electricitySupply', calculationTypeId)"
        class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <Zap
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Electricity supply</CardHeading>
          </div>
        </div>
        <div class="grid grid-cols-12 gap-4">
          <EmissionConsumptionChoice
            v-model="eletricityData"
            :was-validated="props.wasValidated"
            :emission-factor-sources="emissionFactorSources.getElectricitySupplySources()"
            :custom-emission-factor-sources="emissionFactorSources.getElectricitySupplyCustomSources()"
            :emission-factor-types="[Emission_Factor_Type_Enum.ELECTRICITY]"
            :year-of-commissioning="yearOfCommissioning"
            :year-of-construction="yearOfConstruction"
            :consideration-period="considerationPeriod"
            :country="project.buildingAddress?.country"
            variant="installation" />
        </div>
      </div>

      <div
        v-if="configuration.showInstallationField('machineryFuel', calculationTypeId)"
        class="rounded-8 bg-slate-0 p-px-20 flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <Fuel
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Machinery fuel</CardHeading>
          </div>
        </div>
        <template v-if="isBR2025">
          <div class="grid grid-cols-[repeat(8,1fr)_auto] items-center gap-4 text-slate-600">
            <TransportFuelMultipleForm
              :calculation-type-id="calculationTypeId || undefined"
              :consideration-period="considerationPeriod"
              :total-area="totalArea"
              :sources="transportSources"
              @submit="installationEmissionFactors.add" />
          </div>
          <MachineryFuelEmissionFactorTable
            v-if="machineryFuelEmissionFactorItems.length > 0"
            :consideration-period="considerationPeriod"
            :items="_.orderBy(machineryFuelEmissionFactorItems, ['emissionFactorLabel'], ['asc'])"
            :scaled-emission-unit="
              indicators.getIndicatorUnit(calculationTypeIndicator.numerator, calculationTypeIndicator.denominator)
            "
            :total-area="totalArea"
            @remove="installationEmissionFactors.remove" />
        </template>
        <div
          v-else
          class="grid grid-cols-12 gap-4">
          <EmissionConsumptionChoice
            v-model="machineryFuelData"
            :was-validated="props.wasValidated"
            :emission-factor-sources="emissionFactorSources.getFuelSupplySources()"
            :custom-emission-factor-sources="emissionFactorSources.getFuelSupplyCustomSources()"
            :emission-factor-types="[
              Emission_Factor_Type_Enum.DIESEL,
              Emission_Factor_Type_Enum.BIODIESEL,
              Emission_Factor_Type_Enum.GASOLINE,
              Emission_Factor_Type_Enum.BLACK_OIL,
              Emission_Factor_Type_Enum.GAS,
              Emission_Factor_Type_Enum.METHANE,
            ]"
            :year-of-commissioning="yearOfCommissioning"
            :year-of-construction="yearOfConstruction"
            :consideration-period="considerationPeriod"
            :country="project.buildingAddress?.country"
            variant="installation" />
        </div>
      </div>

      <div
        v-if="configuration.showInstallationField('waste', calculationTypeId)"
        class="rounded-8 bg-slate-0 p-px-20 @container flex flex-col gap-3 border border-slate-300">
        <div
          class="-mx-px-20 -mt-px-20 px-px-20 pt-px-20 rounded-t-8 flex flex-col gap-1 border-b border-slate-300 bg-slate-50 pb-3">
          <div class="flex items-center gap-2">
            <span class="bg-app-blue-50 rounded-4 flex shrink-0 items-center justify-center p-1">
              <Recycle
                class="icon-sm text-app-blue-600"
                :stroke-width="1.5"
                aria-hidden="true" />
            </span>
            <CardHeading class="t-sh4 text-type-default">Waste</CardHeading>
          </div>
          <p class="t-caption text-type-muted">
            {{ wastagePercentageDescription }}
          </p>
        </div>
        <div class="grid gap-4 @md:grid-cols-2 @lg:grid-cols-3">
          <NumberInput
            v-model:value="project.emissionCalculationAssumptions.wastagePercentage"
            label="Waste on average (%)"
            color="primary"
            :positive="true"
            :align-right="true" />
        </div>
      </div>
    </div>
  </div>
</template>
